import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Product, ProductFilters, SortOption, ViewMode } from '#types/index';
import { getMinVariantPrice, getMaxVariantPrice, getProductInventory } from '@utils/index';

interface ProductsState {
  products: Product[];
  filteredProducts: Product[];
  filters: ProductFilters;
  sortOption: SortOption;
  viewMode: ViewMode;
  isLoading: boolean;
  error: string | null;
}

const initialState: ProductsState = {
  products: [],
  filteredProducts: [],
  filters: {},
  sortOption: 'price-asc',
  viewMode: 'grid',
  isLoading: false,
  error: null,
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setProducts: (state, action: PayloadAction<Product[]>) => {
      state.products = action.payload;
      state.filteredProducts = action.payload;
    },
    setFilters: (state, action: PayloadAction<ProductFilters>) => {
      state.filters = action.payload;
      // 应用筛选逻辑
      let filtered = [...state.products];
      
      // Category matching (including descendants) is performed by the server.
      if (action.payload.tags && action.payload.tags.length > 0) {
        filtered = filtered.filter((p) =>
          action.payload.tags!.some((tag) => p.tags.includes(tag))
        );
      }
      if (action.payload.minPrice !== undefined) {
        filtered = filtered.filter((p) => (getMinVariantPrice(p) ?? 0) >= action.payload.minPrice!);
      }
      if (action.payload.maxPrice !== undefined) {
        filtered = filtered.filter((p) => (getMaxVariantPrice(p) ?? Infinity) <= action.payload.maxPrice!);
      }
      if (action.payload.inStock !== undefined) {
        filtered = filtered.filter((p) =>
          action.payload.inStock ? getProductInventory(p) > 0 : true
        );
      }
      
      state.filteredProducts = filtered;
    },
    // TODO: 等后端提供带排序的商品列表接口后，改为通过请求参数让后端排序，而不是在前端对 filteredProducts 做本地排序。
    setSortOption: (state, action: PayloadAction<SortOption>) => {
      state.sortOption = action.payload;
      const sorted = [...state.filteredProducts];
      
      switch (action.payload) {
        case 'price-asc':
          sorted.sort((a, b) => (getMinVariantPrice(a) ?? Infinity) - (getMinVariantPrice(b) ?? Infinity));
          break;
        case 'price-desc':
          sorted.sort((a, b) => (getMaxVariantPrice(b) ?? 0) - (getMaxVariantPrice(a) ?? 0));
          break;
        case 'newest':
          sorted.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          break;
        case 'popular':
          // 这里可以根据实际需求实现，比如根据销量排序
          break;
      }
      
      state.filteredProducts = sorted;
    },
    setViewMode: (state, action: PayloadAction<ViewMode>) => {
      state.viewMode = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setProducts,
  setFilters,
  setSortOption,
  setViewMode,
  setLoading,
  setError,
} = productsSlice.actions;
export default productsSlice.reducer;

